import React from 'react';
import PropTypes from 'prop-types';
import { gql, useQuery } from '@common/hooks';

import {
  BaseButton,
  DateWithoutTimeZone,
  ErrorHandler,
  HeaderBar,
  Icon,
  Currency,
  Link,
} from '@common/components';
import { StaffingRequestStateBadge } from 'works/components/staffing-request-state-badge';
import { StateMenu } from 'works/components/state-menu';
import { STAFFING_REQUEST_STATES_FRAGMENT } from '@applicant-flow/graphql/fragments';
import { ENUM_QUERY } from '@common/graphql';
import { buildDialogUrl } from '@common/services';

const STATE_BACKGROUND_MAP = {
  OPEN: 'elm-hyper-hyperlight',
  ON_HOLD: 'sun-lightest',
  FILLED: 'elm-hyper-hyperlight',
  CANCELLED: 'coral-hypyer-hyperlight',
  REJECTED_BY_APPROVER: 'coral-hypyer-hyperlight',
};

function formatShift({ staffingRequestType, shiftsPerWeek, hoursPerShift }) {
  if (staffingRequestType === 'on_demand') {
    return 'Per Diem';
  }

  return `${shiftsPerWeek} x ${hoursPerShift}`;
}

function StaffingRequestDescriptionByType({ type }) {
  const { loading, data, error } = useQuery(ENUM_QUERY, {
    variables: { key: 'StaffingRequestTypes' },
  });

  if (error) {
    return (
      <ErrorHandler
        error={error}
        log="SummaryBar::StaffingRequestDescriptionByType"
        messageType="contextual"
      />
    );
  }

  if (loading || !data) {
    return null;
  }

  const staffingRequestTypeDescription = data.enum.find(
    ({ name }) => name === type
  )?.description;

  return <>{staffingRequestTypeDescription}</>;
}

export function SummaryBar({ showState, showDropDown, staffingRequest }) {
  const displayState = showState && staffingRequest.state !== null;
  const displayDropDown = showDropDown && staffingRequest.state !== null;
  const backgroundColor =
    displayState || displayDropDown
      ? STATE_BACKGROUND_MAP[staffingRequest.state]
      : null;

  const canShowRateDetails = staffingRequest.canShowRateDetails.value;
  const canShowEditDrawer = staffingRequest.canUpdatePositionCount.value;

  return (
    <div className="SummaryBar">
      <HeaderBar background={backgroundColor}>
        {displayDropDown && (
          <HeaderBar.Column lg={3}>
            <div className="state-menu-container">
              <StateMenu staffingRequest={staffingRequest} />
            </div>
          </HeaderBar.Column>
        )}
        {displayState && (
          <HeaderBar.Column lg={4}>
            <StaffingRequestStateBadge
              showPausedSubmissionLabel
              state={staffingRequest.state}
              timestamp={staffingRequest.stateChangedAt}
              submissionsPaused={staffingRequest.submissionsPaused}
            />
          </HeaderBar.Column>
        )}
        <HeaderBar.Column lg={3}>
          <Link
            to={buildDialogUrl('staffing-request-details-drawer', {
              props: { staffingRequestId: staffingRequest.id },
            })}
          >
            <strong>
              {staffingRequest.code}{' '}
              <Icon kind="light" icon="arrowRight" size="sm" />
            </strong>
          </Link>
          <br />
          <span className="secondary-paragraph">{staffingRequest.name} </span>
        </HeaderBar.Column>
        <HeaderBar.Column lg={2} md={2} label="Contract Type">
          <StaffingRequestDescriptionByType
            type={staffingRequest.staffingRequestType}
          />
        </HeaderBar.Column>
        <HeaderBar.Column lg={3}>
          <strong>
            {staffingRequest.organizationUnit.clinicalUnit.displayName}
          </strong>
          <br />
          <span className="secondary-paragraph">
            {
              staffingRequest.organizationUnit.department.organizationBusiness
                .name
            }
          </span>
        </HeaderBar.Column>
        <HeaderBar.Column xs={0} md={1} lg={1} label="Role">
          {staffingRequest.role.abbreviation}
        </HeaderBar.Column>
        <HeaderBar.Column xs={0} md={2} lg={2} label="Type">
          <span className="capitalize">{staffingRequest.shiftType}</span>
        </HeaderBar.Column>
        <HeaderBar.Column xs={0} md={2} lg={2} label="Shift">
          {formatShift(staffingRequest)}
        </HeaderBar.Column>
        <HeaderBar.Column xs={0} md={3} lg={2} label="Start Date">
          <DateWithoutTimeZone date={staffingRequest.startDate} />
        </HeaderBar.Column>
        <HeaderBar.Column xs={0} md={2} lg={2} label="Length">
          {staffingRequest.length} wks
        </HeaderBar.Column>
        {canShowRateDetails && (
          <HeaderBar.Column xs={0} md={0} lg={2} label="Rate" divider={false}>
            {staffingRequest.billingCard ? (
              staffingRequest.lowestRate !== staffingRequest.highestRate ? (
                <>
                  <Currency value={staffingRequest.lowestRate} /> -{' '}
                  <Currency value={staffingRequest.highestRate} />
                </>
              ) : (
                <Currency value={staffingRequest.lowestRate} />
              )
            ) : (
              <Currency value={staffingRequest.billingRegularRate} />
            )}
          </HeaderBar.Column>
        )}
        <HeaderBar.Column xs={0} md={0} lg={1} label="Open" divider={false}>
          {[
            staffingRequest.openStaffingRequestPositionsCount,
            staffingRequest.notCancelledStaffingRequestPositionsCount,
          ].join(' of ')}
        </HeaderBar.Column>
        {canShowEditDrawer && (
          <HeaderBar.Column xs={0} md={0} lg={1} className="edit-column">
            <Link
              to={buildDialogUrl('edit-staffing-request-drawer', {
                props: {
                  staffingRequestId: staffingRequest.id,
                },
              })}
            >
              <BaseButton type="primary" size="small">
                <Icon icon="pencil" size="sm" />
              </BaseButton>
            </Link>
          </HeaderBar.Column>
        )}
      </HeaderBar>
      <style jsx global>{`
        .SummaryBar .StateBadge .state {
          font-size: 16px;
        }
        .HeaderBar .ant-col + .ant-col.edit-column {
          border-left: 0;
        }
        .edit-column {
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .SummaryBar .StateBadge .caption {
          font-size: 11px;
          line-height: 26px;
        }
        .SummaryBar .state-menu-container {
          width: 100px;
        }
      `}</style>
    </div>
  );
}

SummaryBar.propTypes = {
  showState: PropTypes.bool,
  showDropDown: PropTypes.bool,
  staffingRequest: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    stateChangedAt: PropTypes.string.isRequired,
    code: PropTypes.string,
    shiftType: PropTypes.string.isRequired,
    hoursPerShift: PropTypes.number.isRequired,
    shiftsPerWeek: PropTypes.number.isRequired,
    length: PropTypes.number.isRequired,
    startDate: PropTypes.string.isRequired,
    lowestRate: PropTypes.number,
    highestRate: PropTypes.number,
    billingRegularRate: PropTypes.number,
    openStaffingRequestPositionsCount: PropTypes.number.isRequired,
    notCancelledStaffingRequestPositionsCount: PropTypes.number.isRequired,
    staffingRequestType: PropTypes.string.isRequired,
    billingCard: PropTypes.shape({
      id: PropTypes.string,
    }),
    canShowRateDetails: PropTypes.shape({
      value: PropTypes.bool,
    }),
    canUpdatePositionCount: PropTypes.shape({
      value: PropTypes.bool,
    }),
    canCreate: PropTypes.shape({
      value: PropTypes.bool,
    }),
    organization: PropTypes.shape({
      name: PropTypes.string,
    }),
    organizationUnit: PropTypes.shape({
      clinicalUnit: PropTypes.shape({
        displayName: PropTypes.string,
      }),
      department: PropTypes.shape({
        organizationBusiness: PropTypes.shape({
          name: PropTypes.string,
        }),
      }),
    }),
    role: PropTypes.shape({
      abbreviation: PropTypes.string,
    }),
    submissionsPaused: PropTypes.bool,
  }).isRequired,
};

StaffingRequestDescriptionByType.propTypes = {
  type: PropTypes.string,
};

SummaryBar.defaultProps = {
  showDropDown: false,
};

SummaryBar.fragment = gql`
  fragment SummaryBarFragment on StaffingRequest {
    id
    state
    stateChangedAt
    code
    shiftType
    hoursPerShift
    shiftsPerWeek
    startDate
    length
    openStaffingRequestPositionsCount
    notCancelledStaffingRequestPositionsCount
    staffingRequestType
    lowestRate
    highestRate

    ...StaffingRequestStates

    billingCard {
      id
    }

    canCreate {
      value
    }

    canUpdatePositionCount {
      value
    }

    canShowRateDetails {
      value
    }

    role {
      id
      abbreviation
    }

    organization {
      id
      name
    }

    organizationUnit {
      id

      clinicalUnit {
        id
        displayName
      }

      department {
        id

        organizationBusiness {
          id
          name
        }
      }
    }

    organizationBusiness {
      id
      name
    }
  }
  ${STAFFING_REQUEST_STATES_FRAGMENT}
`;
